/* Dynamic Plot code copyright 2020 (c) Tuva Labs Inc. */

// let Tone; // for dynamically importing ToneJS, instead of 'import * as Tone from "tone"'
// TODO: Enable the above line for dynamically importing ToneJS
import * as Tone from "tone";

export const wrapDynDataSonification = function(Dyn) {

    /** persistent class to handle sound for selecting cases */
    class CaseSonifier {
        constructor() {
            // create a synth and connect it to the master output (your speakers)
            /*DISABLE_TONE*/ Dyn.assert( Dyn.sound._toneLib );
            /*DISABLE_TONE*/ this.instrument = new Tone.Synth().toMaster(); // Note: for v13.8.34 use toMaster(), use .toDestination() in v.14.x.x
            /*DISABLE_TONE*/ this.duration = '4n'; // quarter note, see time: https://tonejs.github.io/docs/13.8.25/Type#time
        }
    }

    /** persistent class to handle "playing" a sonification of the current plot */
    class PlotSonifier {
        constructor() {
            // create a synth and connect it to the master output (your speakers)
            /*DISABLE_TONE*/ Dyn.assert( Dyn.sound._toneLib );
            /*DISABLE_TONE*/ this.instrument = new Tone.Synth().toMaster(); // Note: for v13.8.34 use toMaster(), use .toDestination() in v.14.x.x
            this.loop = null;  // Tone.loop()
            this.duration = '4n'; // quarter note, see time: https://tonejs.github.io/docs/13.8.25/Type#time
        }
        isPlaying() {
            /*return false; DISABLE_TONE*/ return(Tone.Transport && Tone.Transport.state === 'started');
        }
        isPaused() {
            /*return false; DISABLE_TONE*/ return(Tone.Transport && Tone.Transport.state === 'paused');
        }
        start( callBack, noteDuration, numNotes ) {
            /*DISABLE_TONE*/ Dyn.assert( this.instrument );
            /*DISABLE_TONE*/ Dyn.assert( this.loop === null );
            /*DISABLE_TONE*/ Tone.Transport.start();
            /*DISABLE_TONE*/ this.loop = new Tone.Loop( callBack, noteDuration );
            /*DISABLE_TONE*/ this.loop.start(0);
            /*DISABLE_TONE*/ console.log("PlotSonifier.start()", numNotes, 'notes of duration', noteDuration, Tone.Transport.state );
            /*DISABLE_TONE*/ Dyn.mediator.publish("sound:StopStartPause", {});
        }
        pause( wantPause ) {
            if( wantPause ) {
                /*DISABLE_TONE*/ Tone.Transport.pause();
            } else {
                /*DISABLE_TONE*/ Tone.Transport.start();
            }
            /*DISABLE_TONE*/ console.log('pauseSonification', Tone.Transport.state );
            /*DISABLE_TONE*/ Dyn.mediator.publish("sound:StopStartPause", {});
        }
        stop() {
            if( this.loop ) {
                this.loop.stop();
                this.loop.dispose();
                this.loop = null;
                console.log("PlotSonifier.stop() loop stopped" );
            }
            /*DISABLE_TONE*/ if( Tone.Transport.state !== 'stopped') {
            /*DISABLE_TONE*/     Tone.Transport.stop(); // stop if 'started' or 'paused'
            /*DISABLE_TONE*/     console.log("PlotSonifier.stop() Transport stopped", Tone.Transport.state );
            /*DISABLE_TONE*/ }
            //console.log('stopSonification', Tone.Transport.state );
            /*DISABLE_TONE*/ Dyn.mediator.publish("sound:StopStartPause", {});
        }

        /**
         * Get beats per minute play speed. Typically one case value note is played per beat,
         *  see setBeatsPerMinute() for details. */
        getBeatsPerMinute() {
            /*return 1; DISABLE_TONE*/ return Dyn.mathUtil.roundIfInteger( Tone.Transport.bpm.value );
        }

        /**
         * Set the 'Play' speed in beats per minute, which corresponds to notes per minute.
         * The default is 120 notes per minute === 120 case sonifications per minute (2 notes per second).
         * DETAILS: The actual Tone.js calculation is:  Tone.Transport having 4 notes per measure by default,
         * and we default to playing quarter notes (PlotSonifier.duration = '4n') which are again 4 notes per measure,
         * so 1 note per beat = 120 notes per minute (2 per second).  If you set bpm to 60, notes will play twice as long,
         * so 60 notes per minute (one per second), or bpm 600 === 600 notes per minute (10 per second).
         * @param bpm
         */
        setBeatsPerMinute( bpm ) {
            if( Dyn.mathUtil.isFinite( bpm )) {
                /*DISABLE_TONE*/ let oldBPM = Tone.Transport.bpm.value;
                /*DISABLE_TONE*/ Tone.Transport.bpm.value = Dyn.mathUtil.clipToMinMax( Dyn.sound._minBPM, Dyn.sound._maxBPM, bpm );
                /*DISABLE_TONE*/ Dyn.mediator.publish("sound:VolumeSpeedUpdate", {});
                //console.log( 'setBeatsPerMinute() changed bpm from', oldBPM, 'to', Tone.Transport.bpm.value );
            }
        }
    }

    /** class to store sonification data for the current plot.
     *  Dyn.sound.playSonification() works with the PlotSonifier and PlotView
     *  to play and give visual feedback for this sonification.
     */
    class NoteSequence {
        constructor() {
            this.noteIndex = 0; // index of the next note to play (at play time)
            this.drawIndex = 0; // index of the next note to visualize (at draw time)
            this.types = [];    // sonification options available in this plot
            this.data = {
                type: undefined,    // current sonification type
                notes: [],          // current sonification note sequence
                // plot adds extra data needed for visualization here
            };  // this.data can be a reference to object in this.types[]
        }
        numNotes() {
            return this.data.notes.length;
        }
        setSonficationType( index ) {
            // set the sonification to use, if we have 1+ choices
            Dyn.assert( this.types.length > index );
            if( this.types.length > index ) {
                this.data = this.types[index];
            }
            Dyn.assert( this.numNotes() >= 0 );
        }
    }

    /** class to handle playing one note for one case-attribute value or one plot element */
    class DataNote {
        constructor() {
            // Tone.js note value can be a string ('A4') or number (frequency in hertz).
            // Tone.Frequency("A4").toFrequency() converts note strings to (440) hertz.
            // Tone.Frequency(440).toNote() converts note numbers to ('A4') note.
            // Tone frequency type: https://tonejs.github.io/docs/13.8.25/Type#frequency
            this.note = Dyn.sound._missingCaseNote;
        }

        /**
         * Convert a numerical data value to a note (sound frequency in hertz)
         * Given the min and max values of the data range.
         * @param value {number}
         * @param min {number}
         * @param max {number}
         * @returns {DataNote}
         */
        numericValueToNote( value, min, max ) {
            if (isFinite(value)) {
                // method is to use linear interpolation from low note to high,
                //      within one octave from C4 (low) to C5 (high)
                let noteMap = new Dyn.mathUtil.CoordMap(
                                Dyn.sound._numericDataNotes.lowNoteHz,
                                Dyn.sound._numericDataNotes.highNoteHz,
                                min,
                                max );
                this.note = noteMap.transform( value );
                //console.log('numericValueToNote',value,min,max,'note:',this.note);
            } else {
                this.note = Dyn.sound._missingCaseNote;
            }
            this.type = 'num';
            return this; // for chaining
        }

        /**
         * categoryIndexToNote
         * @param categoryIndex {number} -- category index, integer 0+
         * @param numCategories {number} -- number of categories, integer 0+
         * @returns {DataNote}
         */
        categoryIndexToNote( categoryIndex, numCategories ) {
            const cd = Dyn.sound._categoricalDataNotes,
                maxNumberOfSemiTones = cd.semiTonesPerOctave * cd.numOctaves;

            if( numCategories >= 1 && categoryIndex >= 0 ) {
                if( numCategories <= maxNumberOfSemiTones ) {
                    // method 1: assign a note (semitone) to each category, centered around the middle note 'C4' ("middle C")
                    // We are using all 12 semitones per octave, and making sure that the lowest and highest note are not too high or low.
                    //  e.g. female='C4', male='C#4' or low='B4, middle='C4', high='C#4'
                    /*DISABLE_TONE*/ let midRangeIndex = Math.floor(numCategories/2), // which category should get the middle note?
                    /*DISABLE_TONE*/     freq = Tone.Frequency(cd.middleSemiTone).transpose(categoryIndex-midRangeIndex);
                    /*DISABLE_TONE*/ this.note = freq.toNote();
                    //console.log('categoryIndexToNote',categoryIndex,'of',numCategories,'note:',this.note,lowNote,Tone.Frequency(lowNote).transpose(numCategories).toNote());
                } else {
                    // if too many categories for method #1, use method 2: linear interpolation within one octave.
                    this.numericValueToNote( categoryIndex, 0, numCategories-1 );
                }
            } else {
                this.note = Dyn.sound._missingCaseNote;
            }
            this.type = 'cat'; // for now we don't care if it was method #1 or #2 or missing
            return this; // for chaining
        }

        noteStringToNote( str ) {
            if( Dyn.sound.isToneJsNoteString( str )) {
                this.note = str;
            } else {
                this.note = Dyn.sound._silentNote;
            }
            this.type = 'tone';
        }

        isSilentNote() {
            return Dyn.dataUtil.isMissing( this.note );
        }
    }

    class DynSound {

        // variables
        static _toneLib = null; // if non-null, Tone has been initialized/started
        static _caseSonifier = null; // utility class for audio trace
        static _plotSonifier = null; // utility class for plot sonification
        static _isAudioTraceOn = false; // start in "on" state

        // constants
        static _missingCaseNote = 'C3'; // low C
        static _silentNote = Dyn.dataUtil.kMissingValue;
        static _numericDataNotes = null;
        // {
        //     lowNoteHz: /*1, DISABLE_TONE*/ Tone.Frequency( "C4" ).toFrequency(), // C4 (middle C) is ~261 hertz,
        //     highNoteHz: /*2, DISABLE_TONE*/  Tone.Frequency( "C5" ).toFrequency(), // C5 is ~523 hertz
        // };
        static _categoricalDataNotes = null;
        // {
        //     semiTonesPerOctave: 12, // (https://en.wikipedia.org/wiki/Chromatic_scale)
        //     middleSemiTone: 'C4', // middle 'C'
        //     numOctaves: 6
        // };
        static _minBPM = 60;
        static _maxBPM = 600;
        static initialBPM = 120;    // this should match default for Tone.transport.bpm.value
        static deltaSpeed = 20;     // constant useful for setBeatsPerMinute()
        static _minDB = -20;        // about 10% volume (limit to avoid confusion with muting)
        static _maxDB = 6;          // about 200% volume (limit to avoid distortion)
        static deltaVolume = 1;    // constant useful for setMasterVolumeDB()
        static initialVolumePercent = 100; // default for getMasterVolumePercent()

        static isAudioInitialized() {
            return !! this._toneLib;
        }

        /**
         * Asynchronously load and initialize Dyn.sound, then perform the given Dyn.sound task.
         * This must be called from a user-initialized browser task, such as the callback from clicking a button.
         * Why? See "The AudioContext was not allowed to start. It must be resumed (or created) after a user gesture on the page. https://goo.gl/7K7WLu"
         */
        static async initAudioAndDo( soundTaskFn ) {
            if( ! Tone ) {
                // import Tone only after user presses a button, to avoid "AudioContext not allowed to start" console message
                //  see https://github.com/Tonejs/Tone.js/issues/443
                let module = await import('tone');
                Tone = module.default;
                console.log("ToneJS imported")
            }
            if( ! this._toneLib ) {
                // start Tone only after user presses a button, to avoid "AudioContext not allowed to start" console message
                //  see https://github.com/Tonejs/Tone.js/blob/dev/README.md#starting-audio
                let started = await Tone.start();
                this._toneLib = Tone;
                console.log("ToneJS started")
            }
            if( ! this._numericDataNotes ) {
                this._numericDataNotes = {
                    lowNoteHz: /*1, DISABLE_TONE*/ Tone.Frequency( "C4" ).toFrequency(), // C4 (middle C) is ~261 hertz,
                    highNoteHz: /*2, DISABLE_TONE*/  Tone.Frequency( "C5" ).toFrequency(), // C5 is ~523 hertz
                };
                this._categoricalDataNotes = {
                    semiTonesPerOctave: 12, // (https://en.wikipedia.org/wiki/Chromatic_scale)
                    middleSemiTone: 'C4', // middle 'C'
                    numOctaves: 6
                };
                // note: _caseSonifier can be initialized if audio trace is ON or OFF (_isAudioTraceOn)
                this._caseSonifier = new CaseSonifier();
                // used with Play or Restart button, independent of audio trace/case sonifier
                this._plotSonifier = new PlotSonifier();
                console.log("Dyn.sound initialized")
            }
            soundTaskFn();
        }

        static _checkSonification( hint ) {
            // mostly obsolete, see this.initAudioAndDo()
            Dyn.assert( this._toneLib, "Dyn.sound not initialized for: "+hint );
        }

        static isToneJsNoteString( str ) {
            // is it a Tone.js-compatible note string (see Tone.Frequency )
            return /^[a-gA-G][#]{0,1}[1-9]$/.test( str );
        }

        static makeNote() {
            return new DataNote();
        }

        static makeNoteSequence() {
            return new NoteSequence();
        }

        static getCaseSonifier() {
            this._checkSonification('case');
            return this._caseSonifier;
        }

        static getPlotSonifier() {
            this._checkSonification('plot');
            return this._plotSonifier;
        }

        static isAudioTraceOn() {
            return this._isAudioTraceOn;
        }

        /**
         * Toggle between sonification 'on' and 'off'.
         * When off, case sonification is silent, and plot sonification has stopped.
         * Typically, sonification is turned on and initialized when
         */
        static setAudioTraceOnOff( wantOn ) {
            if( wantOn ) {
                // turn sonification on, and enable sonification of case selection and data values
                this._checkSonification('case');
                this._isAudioTraceOn = true;
            } else {
                // stop any sonfications and disable sonification of case selection and data values
                this._isAudioTraceOn = false;
            }
            /*DISABLE_TONE*/ Dyn.mediator.publish("sound:StopStartPause", {});
        }

        /** get the master volume, 0==full volume in decibels, see setMasterVolumeDB() */
        static getMasterVolumeDB() {
            this._checkSonification();
            return /*1;DISABLE_TONE*/ Dyn.mathUtil.roundIfInteger( Tone.Master.volume.value ); // Note: for v13.8.34 use .Master, use .Destination in v.14.x.x
        }

        /** get the master volume as an amplitude, 0% - 100%,
         * e.g. 0 dB = 100%, -20 db = 10%, see https://en.wikipedia.org/wiki/Decibel
         * @returns {number}
         */
        static getMasterVolumePercent() {
            return 100 * Math.sqrt(Math.pow(10, this.getMasterVolumeDB()/10));
        }

        /**
         * Set the master volume in decibels (dB), where 0 is full system volume, -10 is 1/10th power, etc (https://en.wikipedia.org/wiki/Decibel)
         */
        static setMasterVolumeDB( newVolumeDB ) {
            let clippedVolume = Dyn.mathUtil.clipToMinMax( this._minDB, this._maxDB, newVolumeDB ); //
            this._checkSonification();
            /*DISABLE_TONE*/ Tone.Master.volume.value = clippedVolume; // Note: for v13.8.34 use .Master, use .Destination in v.14.x.x
            /*DISABLE_TONE*/ Dyn.mediator.publish("sound:VolumeSpeedUpdate", {});
        }

        /**
         * If audio trace is on, play sonification of one case-attribute value.
         */
        static playOneCase( plotView, caseIndex, iconIndex ) {
            if( this.isAudioTraceOn()) {
                let caseSonifier = this.getCaseSonifier(),
                    noteData = plotView.getCaseSonificationData(caseIndex, iconIndex);
                // play a note for the given duration
                if( caseSonifier && ! noteData.isSilentNote()) {
                    caseSonifier.instrument.triggerAttackRelease(noteData.note, caseSonifier.duration);
                }
                console.log( 'playOneCase', 'duration:', caseSonifier.duration, 'note:', noteData.note );
            }
        }

        /**
         * If audio trace is on, play sonfication of one data value (e.g. bar height of a histogram)
         * @param value
         * @param minValue
         * @param maxValue
         */
        static playOneDataValue( value, minValue, maxValue ) {
            if( this.isAudioTraceOn()) {
                let caseSonifier = this.getCaseSonifier(),
                    noteData = this.makeNote().numericValueToNote( value, minValue, maxValue) ;
                // play a note for the given duration
                if( caseSonifier && ! noteData.isSilentNote()) {
                    caseSonifier.instrument.triggerAttackRelease(noteData.note, caseSonifier.duration);
                }
                console.log('playOneDataValue', 'duration:', caseSonifier.duration, 'note:', noteData.note );
            }
        }

        /**
         * If audio trace is on, play sonfication of one category value of the given attribute.
         */
        static playOneCategoryValue( plotView, attributeID, category, isNumericInterval ) {
            if( this.isAudioTraceOn()) {
                let caseSonifier = this.getCaseSonifier(),
                    noteData = plotView.getAttributeCategorySonficationData( attributeID, category, isNumericInterval );
                // play a note for the given duration
                if( caseSonifier && ! noteData.isSilentNote()) {
                    caseSonifier.instrument.triggerAttackRelease(noteData.note, caseSonifier.duration);
                }
                console.log('playOneCategoryValue', 'duration:', caseSonifier.duration, 'note:', noteData.note );
            }
        }

        /**
         * Play, or resume play after being paused, a sequence of notes to sonify the current plot.
         * @param plotView
         */
        static playSonification( plotView ) {
            let plotSonifier = this.getPlotSonifier(),
                noteSequence = plotView.getPlotSonificationData();

            function playNote( time ) {
                // IMPORTANT: no plot/DOM changes are allowed here, see drawNote()
                if( noteSequence.noteIndex < noteSequence.numNotes()) {
                    let noteData = noteSequence.data.notes[ noteSequence.noteIndex ];
                    /*DISABLE_TONE*/ console.log('playNote', noteSequence.noteIndex, 'Hertz:', noteData.note, 'time:', time, Tone.Transport.state );
                    if( ! noteData.isSilentNote()) {
                        plotSonifier.instrument.triggerAttackRelease(noteData.note, plotSonifier.duration, time);
                    }
                } else {
                    plotSonifier.stop(); // to stop sound+callback loop
                    /*DISABLE_TONE*/ console.log('playNote', 'time:', time, Tone.Transport.state );
                }
                ++noteSequence.noteIndex;
                // all DOM changes should be scheduled separately, see https://github.com/Tonejs/Tone.js/wiki/Performance
                /*DISABLE_TONE*/ Tone.Draw.schedule( drawNote, time); //use AudioContext time of the event
            }
            function drawNote( time ) {
                // IMPORTANT: All changes to plot/DOM during sonification should happen here
                plotView.drawNote( noteSequence, noteSequence.drawIndex );
                ++noteSequence.drawIndex;
            }

            this.setAudioTraceOnOff( true ); // turn on automatically, if off
            if( plotSonifier.isPaused()) {
                // unpause and continue the paused sonification
                plotSonifier.pause( false ); // unpause
            } else {
                // clear any old sonfication state and start a new one
                plotSonifier.stop(); // stop old play if any
                if( noteSequence.types.length > 0 ) {
                    noteSequence.setSonficationType(0); // for now pick first sonfication option
                }
                plotSonifier.start( playNote, plotSonifier.duration, noteSequence.numNotes() );
            }
        }

        /**
         * Pause the sonfication/note sequence started by playSonification()
         * @param plotView
         */
        static pauseSonification( plotView ) {
            let plotSonifier = this.getPlotSonifier();
            plotSonifier.pause( true );
        }

        /** toggle between play and pausing the plot sonification */
        static playOrPauseSonification() {
            let plotSonifier = this.getPlotSonifier(),
                plotView = Dyn.plotView;
            if( plotSonifier.isPlaying()) {
                this.pauseSonification( plotView );
            } else {
                this.playSonification( plotView );
            }
            return plotSonifier.isPlaying(); // new state
        }

        /** restart the plot sonification */
        static restartSonification() {
            let plotSonifier = this.getPlotSonifier(),
                plotView = Dyn.plotView;
            plotSonifier.stop();
            this.playSonification( plotView );
            return plotSonifier.isPlaying(); // new state
        }

        /** stop the plot sonification */
        static stopSonification() {
            let plotSonifier = this.getPlotSonifier();
            plotSonifier.stop();
        }

    } // class DynSound
    Dyn.sound = DynSound;

}; // wrapDynDataSonification
